import type { CalidadControlModificacion } from "../../../types/control-modificaciones";

export type CalidadTipoOrigen = "" | "q" | "ma";
export type CalidadTipo = "" | "ncp" | "df";

export type CalidadIncidenciasFilters = {
  numero: string;
  search: string;
  tipo_origen: CalidadTipoOrigen;
  tipo: CalidadTipo;
};

export type CalidadIncidenciasQuery = {
  numero?: string;
  search?: string;
  tipo_origen?: Exclude<CalidadTipoOrigen, "">;
  tipo?: Exclude<CalidadTipo, "">;
  page?: number;
  per_page?: number;
};

export type CalidadIncidencia = {
  id: number;
  numero: number;
  fecha: string | null;
  q: boolean;
  ma: boolean;
  literal: string;
  ncp: boolean;
  df: boolean;
  comentarios_para_cierre: string | null;
  comentarios_cliente_auditor: string | null;
  excel_row: number | null;
  tiene_ficha_ncp?: boolean | null;
  created_at: string | null;
  updated_at: string | null;
};

export type CalidadIncidenciaPayload = {
  fecha: string;
  q: boolean;
  ma: boolean;
  literal: string;
  ncp: boolean;
  df: boolean;
  comentarios_para_cierre: string | null;
  comentarios_cliente_auditor: string | null;
};

export type CalidadIncidenciaFormValues = CalidadIncidenciaPayload & {
  numero: number;
};

export type CalidadIncidenciasMeta = {
  total: number;
  next_numero: number;
  current_page: number;
  last_page: number;
  per_page: number;
  from: number | null;
  to: number | null;
};

export type CalidadIncidenciasResponse = {
  data: CalidadIncidencia[];
  control_modificaciones: CalidadControlModificacion[];
  meta: CalidadIncidenciasMeta;
};

export type CalidadNcpAccionCorrectiva = {
  id: number;
  orden: number;
  analisis_causa: string | null;
  como_solucionar: string | null;
  responsable: string | null;
  plazo: string | null;
  evaluacion_eficacia: string | null;
};

export type CalidadNcpFirmaTipo = "responsable_si" | "direccion";

export type CalidadNcpFirma = {
  id: number;
  tipo: CalidadNcpFirmaTipo;
  usuario_id: number | null;
  usuario_nombre: string;
  usuario_correo: string;
  firmado_en: string | null;
  documento_version: number;
  documento_hash: string;
  metodo: string;
  ip_address: string | null;
  vigente: boolean;
  invalidada_en: string | null;
  invalidacion_motivo: string | null;
};

export type CalidadNcpFicha = {
  id: number;
  incidencia_id: number;
  numero: number | null;
  fecha: string | null;
  literal: string | null;
  descripcion_incidencia: string | null;
  accion_inmediata: string | null;
  responsable_accion_inmediata: string | null;
  plazo_accion_inmediata: string | null;
  comprobacion_solucion: string | null;
  requiere_accion_correctiva: boolean | null;
  firma_responsable_si: boolean;
  firma_direccion: boolean;
  firma_responsable_legacy: boolean;
  firma_direccion_legacy: boolean;
  documento_version: number;
  firmas: CalidadNcpFirma[];
  acciones_correctivas: CalidadNcpAccionCorrectiva[];
  created_at: string | null;
  updated_at: string | null;
};

export type CalidadNcpFichaPayload = {
  descripcion_incidencia: string | null;
  accion_inmediata: string | null;
  responsable_accion_inmediata: string | null;
  plazo_accion_inmediata: string | null;
  comprobacion_solucion: string | null;
  requiere_accion_correctiva: boolean | null;
  acciones_correctivas: Array<{
    analisis_causa: string | null;
    como_solucionar: string | null;
    responsable: string | null;
    plazo: string | null;
    evaluacion_eficacia: string | null;
  }>;
};

export type CalidadNcpFichaResponse = {
  data: CalidadNcpFicha;
};

export const defaultCalidadFilters: CalidadIncidenciasFilters = {
  numero: "",
  search: "",
  tipo_origen: "",
  tipo: "",
};

export function createEmptyCalidadIncidenciaForm(
  nextNumero: number,
): CalidadIncidenciaFormValues {
  return {
    numero: nextNumero,
    fecha: "",
    q: false,
    ma: false,
    literal: "",
    ncp: false,
    df: false,
    comentarios_para_cierre: "",
    comentarios_cliente_auditor: "",
  };
}

export function mapIncidenciaToForm(
  incidencia: CalidadIncidencia,
): CalidadIncidenciaFormValues {
  return {
    numero: incidencia.numero,
    fecha: incidencia.fecha ?? "",
    q: incidencia.q,
    ma: incidencia.ma,
    literal: incidencia.literal,
    ncp: incidencia.ncp,
    df: incidencia.df,
    comentarios_para_cierre: incidencia.comentarios_para_cierre ?? "",
    comentarios_cliente_auditor: incidencia.comentarios_cliente_auditor ?? "",
  };
}

export function mapFormToIncidenciaPayload(
  form: CalidadIncidenciaFormValues,
): CalidadIncidenciaPayload {
  return {
    fecha: form.fecha,
    q: form.q,
    ma: form.ma,
    literal: form.literal.trim(),
    ncp: form.ncp,
    df: form.df,
    comentarios_para_cierre: normalizeNullableString(form.comentarios_para_cierre),
    comentarios_cliente_auditor: normalizeNullableString(form.comentarios_cliente_auditor),
  };
}

export function createEmptyCalidadNcpAction(
  orden: number,
): CalidadNcpAccionCorrectiva {
  return {
    id: 0,
    orden,
    analisis_causa: "",
    como_solucionar: "",
    responsable: "",
    plazo: "",
    evaluacion_eficacia: "",
  };
}

export function createEmptyCalidadNcpFicha(
  incidencia: CalidadIncidencia,
): CalidadNcpFicha {
  return {
    id: 0,
    incidencia_id: incidencia.id,
    numero: incidencia.numero,
    fecha: incidencia.fecha,
    literal: incidencia.literal,
    descripcion_incidencia: "",
    accion_inmediata: "",
    responsable_accion_inmediata: "",
    plazo_accion_inmediata: "",
    comprobacion_solucion: "",
    requiere_accion_correctiva: null,
    firma_responsable_si: false,
    firma_direccion: false,
    firma_responsable_legacy: false,
    firma_direccion_legacy: false,
    documento_version: 1,
    firmas: [],
    acciones_correctivas: [createEmptyCalidadNcpAction(1)],
    created_at: null,
    updated_at: null,
  };
}

export function mapFichaToPayload(
  ficha: CalidadNcpFicha,
): CalidadNcpFichaPayload {
  return {
    descripcion_incidencia: normalizeNullableString(ficha.descripcion_incidencia),
    accion_inmediata: normalizeNullableString(ficha.accion_inmediata),
    responsable_accion_inmediata: normalizeNullableString(
      ficha.responsable_accion_inmediata,
    ),
    plazo_accion_inmediata: normalizeNullableString(
      ficha.plazo_accion_inmediata,
    ),
    comprobacion_solucion: normalizeNullableString(ficha.comprobacion_solucion),
    requiere_accion_correctiva: ficha.requiere_accion_correctiva,
    acciones_correctivas: ficha.acciones_correctivas.map((accion) => ({
      analisis_causa: normalizeNullableString(accion.analisis_causa),
      como_solucionar: normalizeNullableString(accion.como_solucionar),
      responsable: normalizeNullableString(accion.responsable),
      plazo: normalizeNullableString(accion.plazo),
      evaluacion_eficacia: normalizeNullableString(accion.evaluacion_eficacia),
    })),
  };
}

export function formatCalidadDate(value: string | null): string {
  if (!value) {
    return "Sin fecha";
  }

  const [year, month, day] = value.split("-");

  if (!year || !month || !day) {
    return value;
  }

  return `${day}/${month}/${year}`;
}

export function hasActiveCalidadFilters(
  filters: CalidadIncidenciasFilters,
): boolean {
  return (
    filters.numero.trim() !== "" ||
    filters.search.trim() !== "" ||
    filters.tipo_origen !== "" ||
    filters.tipo !== ""
  );
}

export function normalizeNullableString(value: string | null | undefined) {
  if (value === null || value === undefined) {
    return null;
  }

  const trimmed = value.trim();

  return trimmed === "" ? null : trimmed;
}
